import api from './api'
import type { AxiosRequestConfig } from 'axios'

// Dashboard Stats
export interface AdminDashboardStats {
  total_users: number
  active_users: number
  verified_users: number
  superadmin_users: number
  total_organizations: number
  active_organizations: number
  total_projects: number
  total_agents: number
  total_knowledge_bases: number
  total_workflows: number
}

export interface AdminDashboardResponse {
  stats: AdminDashboardStats
}

// User Management
export interface AdminUser {
  id: string
  email: string
  full_name: string | null
  is_active: boolean
  is_verified: boolean
  is_superadmin: boolean
  created_at: string
  last_login: string | null
  organizations_count: number
}

export interface AdminUserListParams {
  search?: string
  status?: 'all' | 'active' | 'inactive' | 'verified' | 'unverified'
  page?: number
  page_size?: number
}

export interface AdminUserListResponse {
  users: AdminUser[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AdminUserDetailResponse extends AdminUser {
  organizations: {
    id: string
    name: string
    role: string
  }[]
}

export interface AdminUserCreateRequest {
  email: string
  full_name?: string | null
  organization_id?: string | null
  organization_role?: string | null
}

export interface AdminUserCreateResponse {
  message: string
  user: AdminUser
}

export interface AdminUserUpdateRequest {
  is_active?: boolean
  is_superadmin?: boolean
}

export interface AdminUserUpdateResponse {
  message: string
  user: AdminUser
}

// Organization Management
export interface AdminOrgOwner {
  id: string
  email: string
  full_name: string | null
}

export interface AdminOrganization {
  id: string
  name: string
  slug: string
  is_active: boolean
  created_at: string
  owner: AdminOrgOwner | null
  members_count: number
  projects_count: number
  agents_count: number
}

export interface AdminOrganizationListParams {
  search?: string
  status?: 'all' | 'active' | 'inactive'
  page?: number
  page_size?: number
}

export interface AdminOrganizationListResponse {
  organizations: AdminOrganization[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AdminOrganizationDetailResponse extends AdminOrganization {
  description: string | null
  updated_at: string
  knowledge_bases_count: number
  workflows_count: number
  members: {
    id: string
    email: string
    full_name: string | null
    role: string
    is_active: boolean
  }[]
}

export interface AdminOrganizationCreateRequest {
  name: string
  slug: string
  description?: string | null
  owner_id?: string | null
}

export interface AdminOrganizationCreateResponse {
  message: string
  organization: AdminOrganization
}

export interface AdminOrganizationUpdateRequest {
  is_active?: boolean
}

export interface AdminOrganizationUpdateResponse {
  message: string
  organization: AdminOrganization
}

export interface AdminOrgAddMemberRequest {
  user_id: string
  role: string
}

export interface AdminOrgAddMemberResponse {
  message: string
  member: {
    id: string
    email: string
    full_name: string | null
    role: string
    is_active: boolean
  }
}

export interface AdminOrgUpdateMemberRoleRequest {
  role: string
}

export interface AdminOrgUpdateMemberRoleResponse {
  message: string
  member: {
    id: string
    email: string
    full_name: string | null
    role: string
    is_active: boolean
  }
}

export const adminService = {
  // Dashboard
  async getDashboard(config?: AxiosRequestConfig): Promise<AdminDashboardResponse> {
    const response = await api.get<AdminDashboardResponse>('/admin/dashboard', config)
    return response.data
  },

  // Users
  async createUser(
    data: AdminUserCreateRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminUserCreateResponse> {
    const response = await api.post<AdminUserCreateResponse>('/admin/users', data, config)
    return response.data
  },

  async listUsers(
    params?: AdminUserListParams,
    config?: AxiosRequestConfig
  ): Promise<AdminUserListResponse> {
    const response = await api.get<AdminUserListResponse>('/admin/users', {
      params,
      ...config,
    })
    return response.data
  },

  async getUser(userId: string, config?: AxiosRequestConfig): Promise<AdminUserDetailResponse> {
    const response = await api.get<AdminUserDetailResponse>(`/admin/users/${userId}`, config)
    return response.data
  },

  async updateUser(
    userId: string,
    data: AdminUserUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminUserUpdateResponse> {
    const response = await api.patch<AdminUserUpdateResponse>(
      `/admin/users/${userId}`,
      data,
      config
    )
    return response.data
  },

  async deleteUser(userId: string, config?: AxiosRequestConfig): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/admin/users/${userId}`, config)
    return response.data
  },

  // Organizations
  async createOrganization(
    data: AdminOrganizationCreateRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminOrganizationCreateResponse> {
    const response = await api.post<AdminOrganizationCreateResponse>(
      '/admin/organizations',
      data,
      config
    )
    return response.data
  },

  async listOrganizations(
    params?: AdminOrganizationListParams,
    config?: AxiosRequestConfig
  ): Promise<AdminOrganizationListResponse> {
    const response = await api.get<AdminOrganizationListResponse>('/admin/organizations', {
      params,
      ...config,
    })
    return response.data
  },

  async getOrganization(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<AdminOrganizationDetailResponse> {
    const response = await api.get<AdminOrganizationDetailResponse>(
      `/admin/organizations/${orgId}`,
      config
    )
    return response.data
  },

  async updateOrganization(
    orgId: string,
    data: AdminOrganizationUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminOrganizationUpdateResponse> {
    const response = await api.patch<AdminOrganizationUpdateResponse>(
      `/admin/organizations/${orgId}`,
      data,
      config
    )
    return response.data
  },

  async deleteOrganization(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      `/admin/organizations/${orgId}`,
      config
    )
    return response.data
  },

  async addOrgMember(
    orgId: string,
    data: AdminOrgAddMemberRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminOrgAddMemberResponse> {
    const response = await api.post<AdminOrgAddMemberResponse>(
      `/admin/organizations/${orgId}/members`,
      data,
      config
    )
    return response.data
  },

  async updateOrgMemberRole(
    orgId: string,
    userId: string,
    data: AdminOrgUpdateMemberRoleRequest,
    config?: AxiosRequestConfig
  ): Promise<AdminOrgUpdateMemberRoleResponse> {
    const response = await api.patch<AdminOrgUpdateMemberRoleResponse>(
      `/admin/organizations/${orgId}/members/${userId}`,
      data,
      config
    )
    return response.data
  },

  async removeOrgMember(
    orgId: string,
    userId: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      `/admin/organizations/${orgId}/members/${userId}`,
      config
    )
    return response.data
  },
}
